import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { Capacitor } from '@capacitor/core'
import { APP_ORIGIN } from '@shared/origin'
import type { Stage, UserProfile } from '../types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// In the native shell the WebView origin is not something we can hand out or
// report: Android serves from https://localhost, iOS from capacitor://localhost.
// Neither resolves off-device, and GA4 can't even parse the iOS one (#382).
// Fall back to the production host when running natively.
export function getAppOrigin(): string {
  return Capacitor.isNativePlatform() ? APP_ORIGIN : window.location.origin
}

// Links people share always use the canonical host, whichever host the app was
// opened on — so a visitor on another served host doesn't mint links an older
// native shell can't open. Dev keeps localhost so shared links stay testable.
export function getShareOrigin(): string {
  return import.meta.env.DEV && !Capacitor.isNativePlatform() ? window.location.origin : APP_ORIGIN
}

// The MCP endpoint is a Hosting rewrite, so the Vite dev server has none —
// point every build at production, where the agent will actually connect.
export function getMcpUrl(): string {
  return `${APP_ORIGIN}/mcp`
}

export function getInviteUrl(inviteCode: string): string {
  return `${getShareOrigin()}/join/${inviteCode}`
}

// Printed on a physical totem, so it stays keyed on raw IDs rather than a
// rotatable code — the tape outlives any code we could revoke.
export function getTotemShareUrl(groupId: string, totemId: string): string {
  return `${getShareOrigin()}/t/${groupId}/${totemId}`
}

// A share link to your schedule for one event, carrying an invite so a viewer
// who joins befriends you (and optionally joins a group). The schedule page reads
// the invite's ownerId to render your picks read-only.
export function getScheduleShareUrl(eventId: string, inviteId: string): string {
  return `${getShareOrigin()}/events/${eventId}/schedule?invite=${inviteId}`
}

export function getFriendInviteUrl(friendCode: string): string {
  return `${getShareOrigin()}/add/${friendCode}`
}

export function getFriendInviteMessage(friendCode: string): string {
  return getFriendInviteUrl(friendCode)
}

export function getInviteMessage(inviteCode: string): string {
  return getInviteUrl(inviteCode)
}

// Deep-link to a single set in an event's schedule: lands on the schedule
// route, switches to the set's day, and flashes the set block (see the
// schedule's ?set= resolver + DayGrid highlight). The schedule looks the set's
// day up itself.
export function scheduleSetPath(eventId: string, setId: string): string {
  const params = new URLSearchParams({ set: setId })
  return `/events/${eventId}/schedule?${params.toString()}`
}

// Two-part public suffixes we actually see on ticket links — without these,
// "eventbrite.co.uk" would brand as "co".
const MULTI_PART_TLDS = new Set(['co.uk', 'com.au', 'co.nz', 'com.br', 'co.za'])

/**
 * The brand a link belongs to, for labelling it in the UI: "partiful", "axs",
 * "dice". Strips www, the public suffix, and any subdomain, so
 * `https://tickets.taogroup.com/...` reads as "taogroup" rather than "tickets".
 * Returns null when there's nothing usable to show.
 */
export function linkBrandLabel(rawUrl: string): string | null {
  let host: string
  try {
    host = new URL(rawUrl.trim()).hostname.replace(/^www\./, '').toLowerCase()
  } catch {
    return null
  }
  const parts = host.split('.').filter(Boolean)
  if (parts.length < 2) return parts[0] ?? null
  const suffixLength = MULTI_PART_TLDS.has(parts.slice(-2).join('.')) ? 2 : 1
  return parts[parts.length - 1 - suffixLength] ?? null
}

export function getInitials(name: string): string {
  return String(name)
    .split(/\s+/)
    .map(w => w[0])
    .join('')
    .toUpperCase()
}

const AVATAR_PALETTE = [
  'bg-rose-500',
  'bg-orange-500',
  'bg-amber-500',
  'bg-lime-500',
  'bg-emerald-500',
  'bg-teal-500',
  'bg-cyan-500',
  'bg-sky-500',
  'bg-blue-500',
  'bg-violet-500',
  'bg-fuchsia-500',
  'bg-pink-500',
] as const

export function getAvatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length]
}

const GRADIENT_PALETTE = [
  '#f43f5e',
  '#f97316',
  '#f59e0b',
  '#84cc16',
  '#10b981',
  '#14b8a6',
  '#06b6d4',
  '#0ea5e9',
  '#3b82f6',
  '#8b5cf6',
  '#d946ef',
  '#ec4899',
] as const

export function getGradientPair(seed: string): [string, string] {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  const a = Math.abs(h) % GRADIENT_PALETTE.length
  const offset = 1 + (Math.abs(h >> 4) % 2)
  const b = (a + offset) % GRADIENT_PALETTE.length
  return [GRADIENT_PALETTE[a], GRADIENT_PALETTE[b]]
}

/**
 * Sort comparator for two user IDs by display name.
 * If `currentUserId` is provided, that user always sorts first.
 * Falls back to empty string for unknown profiles.
 */
export function compareByDisplayName(
  a: string,
  b: string,
  profiles: Record<string, UserProfile>,
  currentUserId?: string | null,
): number {
  if (currentUserId) {
    if (a === currentUserId) return -1
    if (b === currentUserId) return 1
  }
  const nameA = profiles[a]?.displayName?.trim() ?? ''
  const nameB = profiles[b]?.displayName?.trim() ?? ''
  return nameA.localeCompare(nameB)
}

export function getCalendarFeedUrl(token: string): string {
  return `${getShareOrigin()}/cal/${token}.ics`
}

export function compareStageOrder(a: Stage, b: Stage): number {
  const ao = a.order ?? Number.POSITIVE_INFINITY
  const bo = b.order ?? Number.POSITIVE_INFINITY
  if (ao !== bo) return ao - bo
  return a.name.localeCompare(b.name)
}

export const byStartDateAsc = (a: { startDate: string }, b: { startDate: string }) =>
  a.startDate.localeCompare(b.startDate)

export const byStartDateDesc = (a: { startDate: string }, b: { startDate: string }) =>
  b.startDate.localeCompare(a.startDate)
